import type React from 'react';
import { makeStyles, tokens } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import {
    ChevronDoubleLeftRegular,
    ChevronDoubleRightRegular,
    ChevronLeftRegular,
    ChevronRightRegular,
} from '@fluentui/react-icons';

interface TablePaginationProps {
    currentPage: number;
    totalPages: number;
    pageSize: number;
    totalItems: number;
    onPageChange: (page: number) => void;
    datasetReferenceId?: string;
    reportName?: string;
    onExportCsv?: (datasetReferenceId: string) => void;
}

const useStyles = makeStyles({
    pagination: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px',
        borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
        backgroundColor: tokens.colorNeutralBackground1,
        flexShrink: 0,
        gap: '4px',
    },
    button: {
        padding: '4px 8px',
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: tokens.borderRadiusXLarge,
        backgroundColor: tokens.colorNeutralBackground1,
        cursor: 'pointer',
        minWidth: '32px',
        height: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&:hover:not(:disabled)': {
            backgroundColor: tokens.colorBrandBackground2,
        },
        '&:disabled': {
            cursor: 'not-allowed',
            opacity: 0.5,
        },
    },
    pageInfo: {
        padding: '4px 8px',
        backgroundColor: tokens.colorNeutralBackground1,
        minWidth: '200px',
        height: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    paginationControls: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        flex: 1,
        justifyContent: 'center',
    },
    exportButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        minWidth: '150px',
    },
    spacer: {
        minWidth: '150px',
    },
});

const TablePagination = ({
    currentPage,
    totalPages,
    pageSize,
    totalItems,
    onPageChange,
    datasetReferenceId,
    onExportCsv,
}: TablePaginationProps): React.JSX.Element => {
    const classes = useStyles();
    const { t } = useTranslation();

    const startRecord = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const endRecord = Math.min(currentPage * pageSize, totalItems);

    const handleExportCsv = () => {
        if (datasetReferenceId && onExportCsv) {
            onExportCsv(datasetReferenceId);
        }
    };

    return (
        <div className={classes.pagination}>
            {datasetReferenceId && onExportCsv ? (
                <div className={classes.exportButton}>
                    <button className={classes.button} onClick={handleExportCsv} title={t('datatable.exportCsv')}>
                        {t('datatable.exportCsv')}
                    </button>
                </div>
            ) : (
                <div className={classes.spacer} />
            )}
            <div className={classes.paginationControls}>
                <button
                    className={classes.button}
                    onClick={() => {
                        onPageChange(1);
                    }}
                    disabled={currentPage === 1}
                    title={t('datatable.paginate.first')}
                >
                    <ChevronDoubleLeftRegular />
                </button>
                <button
                    className={classes.button}
                    onClick={() => {
                        onPageChange(currentPage - 1);
                    }}
                    disabled={currentPage === 1}
                    title={t('datatable.paginate.previous')}
                >
                    <ChevronLeftRegular />
                </button>
                <div className={classes.pageInfo}>
                    {t('datatable.paginate.showing', { start: startRecord, end: endRecord, total: totalItems })}
                </div>
                <button
                    className={classes.button}
                    onClick={() => {
                        onPageChange(currentPage + 1);
                    }}
                    disabled={currentPage === totalPages}
                    title={t('datatable.paginate.next')}
                >
                    <ChevronRightRegular />
                </button>
                <button
                    className={classes.button}
                    onClick={() => {
                        onPageChange(totalPages);
                    }}
                    disabled={currentPage === totalPages}
                    title={t('datatable.paginate.last')}
                >
                    <ChevronDoubleRightRegular />
                </button>
            </div>
            <div className={classes.spacer} />
        </div>
    );
};

export default TablePagination;
